import {gql} from '@apollo/client';
import {partition} from '@benchling/js-utils';
import {css, StyleSheet} from 'aphrodite';
import React from 'react';
import isEqual from 'react-fast-compare';

import {pluralize} from 'BenchlingUtil';
import {OligoAwareFolderItemType} from 'constants/RegistryConstants';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {
  SchemaFieldsQuery,
  SchemaFieldsQueryVariables,
} from 'molbio/bulk-assembly/assemble-modal/__generated/SchemaFieldsQuery';
import {
  AssemblyEntityCountsByModelType,
  getFieldLinkCompatibilityCountsMultiModel,
  maybeGetPolymerCompatibleLinkTarget,
  SchemaIdToImplementedInterfaces,
} from 'molbio/bulk-assembly/assemble-modal/polymerSchemaLinkCompatibility';
import FieldDefinitionLabel from 'registry/field/FieldDefinitionLabel';
import {SchemaField} from 'schemas/fields/SchemaFieldConstants';
import {
  FONT_PROXIMA_11,
  FONT_PROXIMA_12,
  FONT_PROXIMA_13,
  FONT_PROXIMA_14,
  FONT_WEIGHT_PROXIMA_SEMIBOLD,
  FONT_WEIGHT_PROXIMA_REGULAR,
} from 'styles/FontConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import * as Colors from 'ui-library/Colors';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import Menu from 'ui-library/menus/Menu';
import {MenuWidth} from 'ui-library/menus/MenuConstants';
import Option, {OptionProps} from 'ui-library/menus/Option';
import Section from 'ui-library/menus/Section';

export const SCHEMA_FIELDS_QUERY = gql`
  query SchemaFieldsQuery($id: StrictString!) @skipAudit @engComponent(name: MOLBIO) {
    tagSchema(id: $id) {
      ... on TagSchemaNode {
        id
        fieldsJson
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export interface SingleOption<V> {
  type: 'single-option';
  displayName: string;
  value: V;
  entityModelDetails?: AssemblyEntityCountsByModelType;
  key: string;
  requiresMultiLink: boolean;
}

export interface NestedOption<V> {
  type: 'nested-option';
  color: string;
  displayName: string;
  options: Array<SingleOption<V>>;
  key: string;
}

export type SectionOptions<V> = Array<SingleOption<V> | NestedOption<V>>;

export interface SectionDetails<V> {
  options: SectionOptions<V>;
}

// Note that this is intentionally not a component because menu rendering does special introspection of props like `_role`
// to determine rendering which the menu sub-components set. Creating a component between Menu and Option breaks rendering.
function getOptionContent<V>(
  option: SingleOption<V> | NestedOption<V>,
  schemaIdToImplementedInterfaces: SchemaIdToImplementedInterfaces,
  field: SchemaField,
  onFieldMappingChange: (fieldId: string, value: V | null) => void,
  selectedValue: V | undefined
): JSX.Element {
  if (option.type === 'single-option') {
    const disabledDueToMultiLink = option.requiresMultiLink && !field.isMulti;
    const compatibilityProps: Partial<OptionProps<V>> = {
      isDisabled: disabledDueToMultiLink,
      tooltipTitle: disabledDueToMultiLink ? 'This option requires a multi-link field' : undefined,
    };

    if (option.entityModelDetails != null) {
      const {compatibleCount, totalCount} = getFieldLinkCompatibilityCountsMultiModel(
        field,
        option.entityModelDetails,
        schemaIdToImplementedInterfaces
      );
      compatibilityProps.metadata = `${compatibleCount}/${totalCount} sequences`;
      compatibilityProps.isDisabled ||= compatibleCount === 0;
    }

    return (
      <Option
        value={option.value}
        key={option.key}
        onSelect={(value: V) => {
          onFieldMappingChange(field.apiId, value);
        }}
        isSelected={isEqual(selectedValue, option.value)}
        {...compatibilityProps}
      >
        {option.displayName}
      </Option>
    );
  } else {
    const submenuStyle = StyleSheet.create({borderColor: {borderLeftColor: option.color}});
    return (
      <Option contentClassName={css(styles.fragmentDropdown, submenuStyle.borderColor)} key={option.key}>
        {option.displayName}
        <Menu>
          {option.options.map((option) =>
            getOptionContent(
              option,
              schemaIdToImplementedInterfaces,
              field,
              onFieldMappingChange,
              selectedValue
            )
          )}
        </Menu>
      </Option>
    );
  }
}

function getDisplayNameForValue<V>(
  value: V,
  options: Array<SingleOption<V> | NestedOption<V>>
): string | undefined {
  for (const option of options) {
    if (option.type === 'single-option' && isEqual(option.value, value)) {
      return option.displayName;
    } else if (option.type === 'nested-option') {
      const displayName = getDisplayNameForValue(value, option.options);
      if (displayName) {
        return `${option.displayName} / ${displayName}`;
      }
    }
  }
  return undefined;
}

export interface SchemaFieldMapperProps<V> {
  tagSchemaId: string;
  sectionDetails: SectionDetails<V>;
  title: string;
  schemaIdToImplementedInterfaces: SchemaIdToImplementedInterfaces;
  fieldMapping: {[fieldId: string]: V | undefined};
  onFieldMappingChange: (fieldId: string, value: V | null) => void;
}

export default function SchemaFieldMapper<V>({
  sectionDetails,
  tagSchemaId,
  title,
  schemaIdToImplementedInterfaces,
  onFieldMappingChange,
  fieldMapping,
}: SchemaFieldMapperProps<V>): JSX.Element {
  const schemaFieldsResult = useBenchlingQuery<SchemaFieldsQuery, SchemaFieldsQueryVariables>(
    SCHEMA_FIELDS_QUERY,
    {variables: {id: tagSchemaId}}
  );

  if (schemaFieldsResult.type === QueryResultType.PENDING) {
    return <></>;
  }

  if (
    schemaFieldsResult.type === QueryResultType.ERROR ||
    !schemaFieldsResult.data ||
    isInaccessible(schemaFieldsResult.data.tagSchema)
  ) {
    throw new Error(`Error loading schema ${tagSchemaId}`);
  }

  const supportedModelTypes: Array<OligoAwareFolderItemType> = Array.from(
    new Set(
      sectionDetails.options.flatMap((option) => {
        if (option.type === 'single-option') {
          return option.entityModelDetails != null
            ? (Object.keys(option.entityModelDetails) as Array<OligoAwareFolderItemType>)
            : [];
        } else {
          return option.options.flatMap((nestedOption) =>
            nestedOption.entityModelDetails != null
              ? (Object.keys(nestedOption.entityModelDetails) as Array<OligoAwareFolderItemType>)
              : []
          );
        }
      })
    )
  );
  const [supportedFields, unsupportedFields] = partition(
    schemaFieldsResult.data.tagSchema.fieldsJson,
    (fieldJson: SchemaField) => maybeGetPolymerCompatibleLinkTarget(fieldJson, supportedModelTypes) != null
  );

  return (
    <>
      <div
        className={css(styles.border)}
        {...getTestProps({component: 'SchemaFieldMapper', element: 'root', key: title})}
        data-amp-safe={false}
      >
        <div className={css(styles.headerSection)}>
          <div className={css(styles.innerPadding)}>
            <div className={css(styles.title)}>{title}</div>
            <div className={css(styles.row, styles.headerRow)}>
              <div className={css(styles.flexContent)}>
                Field{' '}
                {unsupportedFields.length > 0 && (
                  <Tooltip text="Assembly outputs can only be mapped to applicable entity link fields. All other schema fields are hidden.">
                    <span className={css(styles.grayText)}>
                      <BenchlingIcon color={Colors.GRAY_SEMIDARK} icon="eye-slash" />{' '}
                      {unsupportedFields.length} {pluralize(unsupportedFields.length, 'field')} not shown
                    </span>
                  </Tooltip>
                )}
              </div>
              <div className={css(styles.flexContent)}>Type</div>
              <div className={css(styles.flexContent)}>Value</div>
            </div>
          </div>
        </div>
        <div className={css(styles.innerPadding)}>
          {supportedFields.length === 0 && (
            <div className={css(styles.betweenRowSpacing, styles.baseText)}>
              No entity link schema fields found
            </div>
          )}
          {supportedFields.map((field) => {
            const selectedValue = fieldMapping[field.apiId];
            const mappingTargetDisplayName =
              getDisplayNameForValue(selectedValue, sectionDetails.options) ?? 'Select entities';
            return (
              <div
                key={field.apiId}
                className={css(styles.row, styles.betweenRowSpacing)}
                {...getTestProps({component: 'SchemaFieldMapper', element: 'row', key: field.displayName})}
                data-amp-safe={false}
              >
                <div className={css(styles.baseText, styles.flexContent)}>{field.displayName}</div>
                <div className={css(styles.baseText, styles.flexAligned, styles.flexContent)}>
                  <FieldDefinitionLabel field={field} schemaFieldSelectors={[]} inEditMode={false} />
                </div>
                <div className={css(styles.flexContent)}>
                  <SelectDropdown
                    displayName={mappingTargetDisplayName}
                    menu={
                      <Menu width={MenuWidth.FULL}>
                        <Section>
                          {sectionDetails.options.map((option) =>
                            getOptionContent(
                              option,
                              schemaIdToImplementedInterfaces,
                              field,
                              onFieldMappingChange,
                              selectedValue
                            )
                          )}
                        </Section>
                      </Menu>
                    }
                    closeOnInsideClick={true}
                    closeOnOutsideClick={true}
                    shouldEllipsize={true}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
}

const styles = StyleSheet.create({
  title: {
    ...FONT_PROXIMA_14,
    fontWeight: FONT_WEIGHT_PROXIMA_SEMIBOLD,
    color: Colors.GRAY_DARKER,
    paddingTop: '16px',
  },
  border: {
    border: `1px solid ${Colors.GRAY_LIGHT}`,
    borderRadius: '4px',
  },
  innerPadding: {
    padding: '0 16px',
  },
  headerSection: {
    backgroundColor: Colors.GRAY_LIGHTER,
    borderBottom: `1px solid ${Colors.GRAY_LIGHT}`,
  },
  headerRow: {
    ...FONT_PROXIMA_11,
    fontWeight: FONT_WEIGHT_PROXIMA_SEMIBOLD,
    color: Colors.GRAY_DARKER,
    textTransform: 'uppercase',
    padding: '8px 0',
  },
  row: {
    display: 'flex',
    gap: '8px',
  },
  betweenRowSpacing: {
    margin: '8px 0',
  },
  flexContent: {
    flex: 1,
    alignContent: 'center',
  },
  baseText: {
    ...FONT_PROXIMA_13,
  },
  flexAligned: {
    display: 'flex',
    alignItems: 'center',
  },
  grayText: {
    ...FONT_PROXIMA_12,
    color: Colors.GRAY_SEMIDARK,
    textTransform: 'none',
    fontWeight: FONT_WEIGHT_PROXIMA_REGULAR,
    marginLeft: '8px',
  },
  fragmentDropdown: {
    borderLeftStyle: 'solid',
    borderLeftWidth: '4px',
  },
});
